'use client';

import {
  useState,
  useCallback,
  type ReactNode,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import { UploadIcon } from './UploadIcon';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('FileUploadDropZone');

export interface FileUploadDropZoneProps {
  onFilesSelected?: (files: File[]) => void;
  /** Native file dialog closed without a selection. */
  onCancel?: () => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
  title?: string;
  description?: string;
  icon?: ReactNode;
  maxFiles?: number;
  transparent?: boolean;
}

export interface FileUploadDropZoneRef {
  openFileDialog: () => void;
}

export const FileUploadDropZone = forwardRef<
  FileUploadDropZoneRef,
  FileUploadDropZoneProps
>(function FileUploadDropZone(
  {
    onFilesSelected,
    onCancel,
    accept = 'image/*',
    multiple = false,
    disabled = false,
    className,
    children,
    title,
    description,
    icon,
    maxFiles,
    transparent = false,
  },
  ref
) {
  const t = useTranslations('fileUpload');
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      if (disabled) return;

      const fileArray = Array.from(files);

      // Filter files by accept attribute if specified
      const filteredFiles =
        accept && accept !== '*' && accept !== 'image/*'
          ? fileArray.filter((file) => {
              const acceptTypes = accept.split(',').map((s) => s.trim());
              return acceptTypes.some((type) => {
                if (type === 'image/*') {
                  return file.type.startsWith('image/');
                }
                if (type.startsWith('.')) {
                  return file.name.toLowerCase().endsWith(type.toLowerCase());
                }
                return file.type === type;
              });
            })
          : fileArray;

      if (filteredFiles.length === 0) return;

      // maxFiles is NOT applied here — UnifiedImageUpload.handleFilesReceived
      // is the single truncation point and surfaces a toast when files are
      // dropped past the cap (silently slicing here hid that from the user).
      const filesToProcess = multiple
        ? filteredFiles
        : [filteredFiles[0]].filter((file): file is File => file !== undefined);

      onFilesSelected?.(filesToProcess);
    },
    [disabled, accept, multiple, onFilesSelected]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragging to false if we're leaving the element entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFiles(files);
      }
    },
    [disabled, handleFiles]
  );

  const handleClick = useCallback(() => {
    if (disabled) return;

    logger.info('Creating file input for upload');

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = multiple;

    // Hide the input element
    input.style.position = 'fixed';
    input.style.top = '-100px';
    input.style.left = '-100px';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';

    const cleanup = () => {
      logger.debug('Cleaning up file input');
      try {
        if (input.parentNode) {
          input.parentNode.removeChild(input);
        }
      } catch (error) {
        console.error('[FileUploadDropZone] Error removing input:', error);
      }
    };

    input.onchange = (e: Event) => {
      logger.debug('File input change event fired');
      const target = e.target as HTMLInputElement;

      try {
        if (target.files && target.files.length > 0) {
          logger.info(
            `${target.files.length} file(s) selected`,
            Array.from(target.files).map((f) => ({
              name: f.name,
              size: f.size,
              type: f.type,
            }))
          );
          handleFiles(target.files);
        } else {
          logger.debug('No files selected or cancelled');
          onCancel?.();
        }
      } catch (error) {
        console.error(
          '[FileUploadDropZone] Error handling selected files:',
          error
        );
      } finally {
        // Cleanup after a delay to ensure iOS completes the file selection
        setTimeout(cleanup, 1000);
      }
    };

    // Handle cancellation (when user closes picker without selecting)
    input.oncancel = () => {
      logger.debug('File selection cancelled');
      onCancel?.();
      setTimeout(cleanup, 1000);
    };

    try {
      // Attach to DOM before clicking (required for iOS Safari)
      document.body.appendChild(input);
      logger.debug('File input attached to DOM');

      // Small delay to ensure DOM attachment on iOS
      setTimeout(() => {
        try {
          input.click();
          logger.debug('File input clicked');
        } catch (error) {
          console.error('[FileUploadDropZone] Error clicking input:', error);
          cleanup();
        }
      }, 100);
    } catch (error) {
      console.error('[FileUploadDropZone] Error creating file input:', error);
      cleanup();
    }
  }, [disabled, accept, multiple, handleFiles, onCancel]);

  // Expose openFileDialog method to parent components via ref
  useImperativeHandle(
    ref,
    () => ({
      openFileDialog: handleClick,
    }),
    [handleClick]
  );

  return (
    <div
      className={cn(
        'relative transition-all',
        transparent
          ? ''
          : cn(
              'rounded-lg cursor-pointer border-2 border-dashed',
              isDragging
                ? 'border-primary bg-primary/5 scale-[1.02]'
                : 'border-muted-foreground/30 hover:border-muted-foreground/50'
            ),
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={transparent ? undefined : handleClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children || (
        <div className="p-8 text-center">
          {icon || (
            <UploadIcon className="mx-auto h-12 w-12 mb-3 text-muted-foreground" />
          )}
          {title && (
            <p className="text-muted-foreground font-medium">{title}</p>
          )}
          {description && (
            <p className="text-form-help text-muted-foreground/80 mt-2">
              {description}
            </p>
          )}
          {!title && !description && (
            <>
              <p className="text-muted-foreground font-medium">
                {multiple ? t('clickToSelectFiles') : t('clickToSelectFile')}
              </p>
              {maxFiles && multiple && (
                <p className="text-form-help text-muted-foreground/80 mt-2">
                  {t('maxFilesHint', { count: maxFiles })}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {isDragging && (
        <div className="absolute inset-0 rounded-lg bg-primary/10 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <UploadIcon className="mx-auto h-16 w-16 mb-2 text-primary animate-pulse" />
            <p className="text-primary font-semibold">
              {multiple ? t('dropFilesHere') : t('dropFileHere')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
});
